import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { usePoolData } from '../hooks/usePoolData.js';
import { normalPDF } from '../utils/poissonCalculations.js';

// Format time interval without seconds for graph labels
const formatTimeIntervalNoSeconds = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    if (seconds === Infinity || seconds === -Infinity) {
      return '∞';
    }
    return 'N/A';
  }
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.length > 0 ? parts.join(' ') : '0m';
};

function NormalDistributionGraphComponent() {
  const { predictionData, analysis, blocks } = usePoolData();
  const [xAxisDomain, setXAxisDomain] = React.useState([0, 'auto']);
  const [zoomLevel, setZoomLevel] = React.useState(1);
  const chartContainerRef = React.useRef(null);

  if (!predictionData || !analysis || !blocks) {
    return (
      <div className="bg-navy-light border border-dark p-6 rounded">
        <p className="text-primary">No data available for normal distribution graph</p>
      </div>
    );
  }

  // Calculate observed normal distribution from block intervals
  let observedNormalMean = null;
  let observedNormalStdDev = null;
  let blockIntervals = [];
  
  if (blocks && blocks.length >= 2) {
    // Calculate intervals between consecutive blocks (sorted by timestamp descending)
    const sortedBlocks = [...blocks].sort((a, b) => b.timestamp - a.timestamp);
    const intervals = [];
    for (let i = 0; i < sortedBlocks.length - 1; i++) {
      const interval = sortedBlocks[i].timestamp - sortedBlocks[i + 1].timestamp;
      if (interval > 0 && Number.isFinite(interval)) {
        intervals.push(interval);
      }
    }
    
    blockIntervals = intervals;
    
    if (intervals.length > 0) {
      // Calculate mean and standard deviation
      const sum = intervals.reduce((acc, val) => acc + val, 0);
      observedNormalMean = sum / intervals.length;
      const variance = intervals.reduce((acc, val) => acc + Math.pow(val - observedNormalMean, 2), 0) / intervals.length;
      observedNormalStdDev = Math.sqrt(variance);
    }
  }
  
  // Calculate expected normal distribution based on deviation and expected time
  let expectedNormalMean = null;
  let expectedNormalStdDev = null;
  if (Number.isFinite(analysis.expectedTime) && analysis.expectedTime > 0) {
    expectedNormalMean = analysis.expectedTime;
    // For exponential distribution, stdDev = mean, but we can adjust based on z-score
    const zScore = Math.abs(analysis.deviation?.zScore || 0);
    // Use expected time as base stdDev, adjusted by deviation
    expectedNormalStdDev = analysis.expectedTime * (1 + zScore * 0.1); // Scale z-score impact
  }

  // Determine the time range for the graph
  // Use the maximum of: expected time * 3, max block interval, or 24 hours
  const maxInterval = blockIntervals.length > 0 ? Math.max(...blockIntervals) : 0;
  const maxTime = Math.max(
    expectedNormalMean ? expectedNormalMean * 3 : 0,
    maxInterval * 1.5,
    86400 // 24 hours minimum
  );
  const minTime = 0;
  const numPoints = 200;

  // Generate normal distribution curves
  const chartData = [];
  for (let i = 0; i <= numPoints; i++) {
    const timeSeconds = minTime + (maxTime - minTime) * (i / numPoints);
    const timeHours = timeSeconds / 3600;
    
    let observedNormal = 0;
    let expectedNormal = 0;
    
    if (observedNormalMean !== null && observedNormalStdDev !== null && observedNormalStdDev > 0) {
      observedNormal = normalPDF(timeSeconds, observedNormalMean, observedNormalStdDev);
    }
    
    if (expectedNormalMean !== null && expectedNormalStdDev !== null && expectedNormalStdDev > 0) {
      expectedNormal = normalPDF(timeSeconds, expectedNormalMean, expectedNormalStdDev);
    }
    
    chartData.push({
      timeHours,
      timeSeconds,
      observedNormal,
      expectedNormal,
    });
  }

  // Normalize normal distributions to 0-1 range
  // Find the maximum PDF value across both distributions for normalization
  const maxObservedNormal = chartData.length > 0 ? Math.max(...chartData.map(d => d.observedNormal), 0) : 0;
  const maxExpectedNormal = chartData.length > 0 ? Math.max(...chartData.map(d => d.expectedNormal), 0) : 0;
  const maxNormal = Math.max(maxObservedNormal, maxExpectedNormal);
  
  // If maxNormal is 0 or very small, use a default to prevent division issues
  const normalizationFactor = maxNormal > 0.0000001 ? maxNormal : 1;
  
  const normalizedChartData = chartData.map(d => ({
    ...d,
    observedNormal: normalizationFactor > 0 ? d.observedNormal / normalizationFactor : 0,
    expectedNormal: normalizationFactor > 0 ? d.expectedNormal / normalizationFactor : 0,
  }));

  // Prepare block points - merge into chart data for better compatibility
  // Each block point represents the interval since the previous block
  const blockPointsMap = new Map();
  if (blockIntervals.length > 0 && normalizationFactor > 0) {
    blockIntervals.forEach((interval) => {
      const timeHours = interval / 3600;
      // Calculate the normalized y-value based on the observed normal distribution
      let yValue = 0;
      if (observedNormalMean !== null && observedNormalStdDev !== null && observedNormalStdDev > 0) {
        const pdfValue = normalPDF(interval, observedNormalMean, observedNormalStdDev);
        yValue = normalizationFactor > 0 ? pdfValue / normalizationFactor : 0;
      }
      // Round to nearest 0.1 hours to match chart data points
      const roundedTime = Math.round(timeHours * 10) / 10;
      if (!blockPointsMap.has(roundedTime)) {
        blockPointsMap.set(roundedTime, {
          timeHours: roundedTime,
          blockPoint: yValue,
          timeSeconds: interval,
        });
      }
    });
  }
  
  // Merge block points into chart data
  const finalChartData = normalizedChartData.map(d => {
    const roundedTime = Math.round(d.timeHours * 10) / 10;
    const blockPoint = blockPointsMap.get(roundedTime);
    return {
      ...d,
      blockPoint: blockPoint ? blockPoint.blockPoint : null,
      blockTimeSeconds: blockPoint ? blockPoint.timeSeconds : null,
    };
  });
  
  // Also create separate data points for exact block positions (even if not aligned with chart grid)
  // These will be added to the main chart data for proper rendering
  const exactBlockPoints = [];
  if (blockIntervals.length > 0 && normalizationFactor > 0) {
    blockIntervals.forEach((interval) => {
      const timeHours = interval / 3600;
      let yValue = 0;
      if (observedNormalMean !== null && observedNormalStdDev !== null && observedNormalStdDev > 0) {
        const pdfValue = normalPDF(interval, observedNormalMean, observedNormalStdDev);
        yValue = normalizationFactor > 0 ? pdfValue / normalizationFactor : 0;
      }
      exactBlockPoints.push({
        timeHours,
        blockPoint: yValue,
        timeSeconds: interval,
        observedNormal: 0,
        expectedNormal: 0,
      });
    });
  }
  
  // Merge exact block points into final chart data
  const allDataPoints = [...finalChartData];
  exactBlockPoints.forEach(blockPoint => {
    // Check if this time already exists in chart data
    const existingIndex = allDataPoints.findIndex(d => Math.abs(d.timeHours - blockPoint.timeHours) < 0.01);
    if (existingIndex >= 0) {
      // Update existing point
      allDataPoints[existingIndex].blockPoint = blockPoint.blockPoint;
      allDataPoints[existingIndex].blockTimeSeconds = blockPoint.timeSeconds;
    } else {
      // Add new point
      allDataPoints.push(blockPoint);
    }
  });
  
  // Sort by timeHours
  allDataPoints.sort((a, b) => a.timeHours - b.timeHours);
  
  // Calculate the actual max time from data
  const maxTimeHours = allDataPoints.length > 0 
    ? Math.max(...allDataPoints.map(d => d.timeHours), 24)
    : 24;
  
  // Handle mouse wheel zoom
  const handleWheel = React.useCallback((e) => {
    // Prevent default scrolling
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate zoom
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1; // Zoom out on scroll down, zoom in on scroll up
    
    setXAxisDomain(prevDomain => {
      // Calculate new domain based on current domain
      const currentMax = typeof prevDomain[1] === 'number' ? prevDomain[1] : maxTimeHours;
      const currentMin = typeof prevDomain[0] === 'number' ? prevDomain[0] : 0;
      const currentRange = currentMax - currentMin;
      const center = (currentMin + currentMax) / 2;
      const newRange = currentRange / zoomFactor;
      const newMin = Math.max(0, center - newRange / 2);
      const newMax = Math.min(maxTimeHours, center + newRange / 2);
      
      return [newMin, newMax];
    });
  }, [maxTimeHours]);
  
  // Debug logging
  React.useEffect(() => {
    console.log('=== NormalDistributionGraph Debug ===');
    console.log('observedNormalMean:', observedNormalMean);
    console.log('observedNormalStdDev:', observedNormalStdDev);
    console.log('expectedNormalMean:', expectedNormalMean);
    console.log('expectedNormalStdDev:', expectedNormalStdDev);
    console.log('blockIntervals.length:', blockIntervals.length);
    console.log('exactBlockPoints.length:', exactBlockPoints.length);
    console.log('maxNormal:', maxNormal);
    console.log('normalizedChartData.length:', normalizedChartData.length);
    console.log('allDataPoints.length:', allDataPoints.length);
    console.log('normalizedChartData sample:', normalizedChartData.slice(0, 3));
    console.log('====================================');
  }, [observedNormalMean, observedNormalStdDev, expectedNormalMean, expectedNormalStdDev, blockIntervals.length, exactBlockPoints.length, maxNormal]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const timeHours = data.timeHours || data.x || 0;
      return (
        <div className="bg-navy border border-dark p-3 rounded shadow-lg">
          <p className="text-primary mb-2">
            <span className="text-accent-orange">Time:</span> {timeHours.toFixed(2)}h
          </p>
          {data.observedNormal !== undefined && data.observedNormal > 0 && (
            <p className="text-primary mb-1">
              <span className="text-[#00ff88]">Observed Normal:</span> {(data.observedNormal * 100).toFixed(2)}%
            </p>
          )}
          {data.expectedNormal !== undefined && data.expectedNormal > 0 && (
            <p className="text-primary mb-1">
              <span className="text-[#88ccff]">Expected Normal:</span> {(data.expectedNormal * 100).toFixed(2)}%
            </p>
          )}
          {(data.blockPoint !== undefined && data.blockPoint !== null && data.blockPoint > 0) && (
            <>
              <p className="text-primary mb-1">
                <span className="text-accent-orange">Block Point:</span> {(data.blockPoint * 100).toFixed(2)}%
              </p>
              {data.blockTimeSeconds !== undefined && data.blockTimeSeconds !== null && (
                <p className="text-primary mb-1">
                  <span className="text-accent-orange">Interval:</span> {formatTimeIntervalNoSeconds(data.blockTimeSeconds)}
                </p>
              )}
            </>
          )}
        </div>
      );
    }
    return null;
  };
  
  // Check if we have valid data to display
  const hasData = normalizedChartData.length > 0 && (
    (observedNormalMean !== null && observedNormalStdDev !== null && maxObservedNormal > 0) ||
    (expectedNormalMean !== null && expectedNormalStdDev !== null && maxExpectedNormal > 0)
  );
  
  if (!hasData) {
    return (
      <div className="bg-navy-light border border-dark p-6 rounded">
        <h2 className="text-primary text-xl font-bold mb-4">Normal Distribution Analysis</h2>
        <p className="text-primary">Insufficient data to display normal distribution. Need at least 2 blocks for observed distribution.</p>
      </div>
    );
  }

  return (
    <div className="bg-navy-light border border-dark p-6 rounded">
      <h2 className="text-primary text-xl font-bold mb-4">Normal Distribution Analysis</h2>
      <div 
        ref={chartContainerRef}
        className="w-full h-[400px] sm:h-[500px]"
        onWheel={handleWheel}
        style={{ cursor: 'grab' }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={allDataPoints}
            margin={{ top: 20, right: 20, bottom: 30, left: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#1a2332" />
            <XAxis
              dataKey="timeHours"
              type="number"
              domain={xAxisDomain}
              label={{ value: 'Time Interval (hours)', position: 'insideBottom', offset: -5, fill: '#ffffff' }}
              stroke="#ffffff"
              tick={{ fill: '#ffffff' }}
            />
            <YAxis
              label={{ value: 'Normalized Probability', angle: -90, position: 'insideLeft', fill: '#ffffff' }}
              stroke="#ffffff"
              tick={{ fill: '#ffffff' }}
              domain={[0, 'auto']}
              allowDataOverflow={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: '#ffffff' }} />
            
            {/* Observed normal distribution */}
            {observedNormalMean !== null && observedNormalStdDev !== null && maxObservedNormal > 0 && (
              <Line
                type="monotone"
                dataKey="observedNormal"
                stroke="#00ff88"
                strokeWidth={2}
                dot={false}
                name="Observed Normal Distribution"
                strokeDasharray="5 5"
              />
            )}
            
            {/* Expected normal distribution */}
            {expectedNormalMean !== null && expectedNormalStdDev !== null && maxExpectedNormal > 0 && (
              <Line
                type="monotone"
                dataKey="expectedNormal"
                stroke="#88ccff"
                strokeWidth={2}
                dot={false}
                name="Expected Normal Distribution"
                strokeDasharray="3 3"
              />
            )}
            
            {/* Block points as line with dots only */}
            {exactBlockPoints.length > 0 && (
              <Line
                type="monotone"
                dataKey="blockPoint"
                stroke="none"
                strokeWidth={0}
                dot={{ fill: '#ff8c42', r: 5, strokeWidth: 0 }}
                activeDot={{ r: 7, fill: '#ff8c42' }}
                name="Block Intervals"
                connectNulls={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 text-sm text-primary space-y-1">
        {observedNormalMean !== null && observedNormalStdDev !== null && (
          <p>
            <span className="text-[#00ff88]">Observed:</span> Mean = {formatTimeIntervalNoSeconds(observedNormalMean)}, 
            StdDev = {formatTimeIntervalNoSeconds(observedNormalStdDev)}
          </p>
        )}
        {expectedNormalMean !== null && expectedNormalStdDev !== null && (
          <p>
            <span className="text-[#88ccff]">Expected:</span> Mean = {formatTimeIntervalNoSeconds(expectedNormalMean)}, 
            StdDev = {formatTimeIntervalNoSeconds(expectedNormalStdDev)}
          </p>
        )}
        {blockIntervals.length > 0 && (
          <p>
            <span className="text-accent-orange">Block Points:</span> {blockIntervals.length} intervals plotted
          </p>
        )}
      </div>
    </div>
  );
}

// Memoize NormalDistributionGraph to prevent unnecessary re-renders
export const NormalDistributionGraph = React.memo(NormalDistributionGraphComponent, (prevProps, nextProps) => {
  return false; // Always allow re-render and let React.memo handle shallow comparison
});

